import React from "react";
import { Link } from "react-router-dom";
import { Package, FileDown, PenSquare, LoaderCircle, FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";
import { RoleHelper } from "src/helpers";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { Product, POProductConfirmation, PurchaseOrder } from "src/types";
import { useAuthorisation } from "src/context";
import { ProductTable } from "src/components/tables";
import { VendorFileType } from "src/enums";
import { formatFloatWithTwoDecimals } from "src/utils";
import { DD_MM_YYYY } from "src/helpers";

export interface PurchaseOrderProductsSectionProps {
  products: Product[];
  productConfirmations?: POProductConfirmation[];
  isProductsAmendable: boolean;
  purchaseOrderId: string;
  showPODownloadButton: boolean;
  downloadPDF: () => void;
  downloadCSV: () => void;
  isFileDownloading?: boolean;
  purchaseOrder?: PurchaseOrder;
}

const PurchaseOrderProductsSection: React.FC<PurchaseOrderProductsSectionProps> = ({
  products,
  productConfirmations,
  isProductsAmendable,
  purchaseOrderId,
  showPODownloadButton,
  downloadPDF,
  downloadCSV,
  isFileDownloading,
  purchaseOrder
}) => {
  const { userRole } = useAuthorisation();

  const downloadProductsXlsx = () => {
    const headers = [
      "Id",
      "Name",
      "SKU",
      "Size",
      "Extra Info",
      "Barcode",
      "Unit Cost (£)",
      "HMRC Unit Cost (£)",
      "Order Qty",
      "Confirmed Qty",
      "Est Restock Date (DD/MM/YYYY)"
    ];

    const rows = products.map((p) => {
      const confirmation = productConfirmations?.find((c) => c.productId === p.id);
      const estRestockDisplay = confirmation?.isDiscontinued
        ? "Discontinued"
        : confirmation?.estRestockDate
          ? DD_MM_YYYY.formatDate(new Date(confirmation.estRestockDate))
          : "";

      return [
        p.id,
        p.name,
        p.skuName ?? "",
        p.size ?? "",
        p.extraInfo ?? "",
        p.barcode ?? "",
        p.unitCost != null ? formatFloatWithTwoDecimals(p.unitCost) : "",
        p.hmrcUnitCost != null ? formatFloatWithTwoDecimals(p.hmrcUnitCost) : "",
        p.quantity,
        confirmation?.confirmedQty ?? "",
        estRestockDisplay
      ];
    });

    const wsData = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = [
      { wch: 28 },
      { wch: 32 },
      { wch: 14 },
      { wch: 10 },
      { wch: 18 },
      { wch: 18 },
      { wch: 14 },
      { wch: 18 },
      { wch: 12 },
      { wch: 14 },
      { wch: 28 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Products");
    const filename = `${purchaseOrderId}-products.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  if (!products?.length) {
    return null;
  }

  return (
    <section className="rounded-2xl border border-secondary-200 bg-white p-6 shadow-soft sm:p-8">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-4">
          <div className="rounded-xl bg-secondary-100 p-3 text-secondary-900">
            <Package className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-secondary-900">Products</h2>
            <p className="mt-1 text-sm text-secondary-600">
              Overview of ordered SKUs with confirmation quantities and restock dates from the vendor.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={downloadProductsXlsx}
            className="inline-flex items-center rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-2 text-sm font-medium text-secondary-700 transition-colors duration-200 hover:bg-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2"
          >
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Download Products (XLSX)
          </button>
          {RoleHelper.isHealfOperationsTeam(userRole) && isProductsAmendable && (
            <Link
              to={`${ROUTES_CONFIG.amendPurchaseOrder.path}?id=${purchaseOrderId}`}
              className="inline-flex items-center rounded-lg border border-primary-200 bg-primary-50 px-4 py-2 text-sm font-medium text-primary-700 transition-colors duration-200 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <PenSquare className="mr-2 h-4 w-4" />
              Amend Products
            </Link>
          )}
        </div>
      </div>

      <div className="mt-6">
        <ProductTable
          products={products ?? []}
          productConfirmationData={productConfirmations}
          vendorLocation={purchaseOrder?.origin}
          hmrcExchangeRates={purchaseOrder?.hmrcExchangeRates}
        />
      </div>

      {showPODownloadButton && (
        <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-3 text-sm text-secondary-600">
            <span className="rounded-full border border-secondary-200 bg-secondary-50 px-3 py-1 uppercase tracking-wide text-xs font-semibold text-secondary-700">
              {purchaseOrder?.fileType ?? VendorFileType.PDF}
            </span>
            <span>Download the purchase order or resend to vendors as needed.</span>
          </div>

          {isFileDownloading ? (
            <div className="flex items-center justify-center gap-2 text-sm text-secondary-600">
              <LoaderCircle className="h-5 w-5 animate-spin text-secondary-900" />
              <span>Preparing download...</span>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={downloadCSV}
                className="inline-flex items-center justify-center rounded-lg bg-secondary-900 px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-secondary-800 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2"
              >
                <FileDown className="mr-2 h-4 w-4" />
                Download Purchase Order (CSV)
              </button>
              <button
                type="button"
                onClick={downloadPDF}
                className="inline-flex items-center justify-center rounded-lg bg-secondary-900 px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-secondary-800 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2"
              >
                <FileDown className="mr-2 h-4 w-4" />
                Download Purchase Order (PDF)
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export { PurchaseOrderProductsSection };
